// v3.36.9 · Red de seguridad global para errores de render.
// Si un componente tira error, en vez de pantalla blanca el usuario ve un
// fallback amigable con "Volver al inicio" / "Reintentar" y el error se
// reporta automáticamente al backend (/api/client-errors) para debug.
import React from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function reportClientError(payload) {
  try {
    const body = JSON.stringify({
      message: String(payload.message || '').slice(0, 2000),
      stack: String(payload.stack || '').slice(0, 8000),
      url: typeof window !== 'undefined' ? window.location.href : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      component_stack: String(payload.component_stack || '').slice(0, 8000),
      kind: payload.kind || 'render',
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`${BACKEND_URL}/api/client-errors`, blob);
    } else {
      fetch(`${BACKEND_URL}/api/client-errors`, {
        method: 'POST', body, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) { /* no romper el fallback por error de reporte */ }
}

// Hooks globales para errores NO-React (promise rejections + exceptions sueltas)
if (typeof window !== 'undefined' && !window.__client_err_hooked__) {
  window.__client_err_hooked__ = true;
  window.addEventListener('error', (e) => {
    reportClientError({ message: e.message, stack: e.error?.stack, kind: 'window.onerror' });
  });
  window.addEventListener('unhandledrejection', (e) => {
    reportClientError({ message: String(e.reason), stack: e.reason?.stack, kind: 'unhandledrejection' });
  });
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    reportClientError({
      message: error?.message || String(error),
      stack: error?.stack,
      component_stack: info?.componentStack,
      kind: 'render',
    });
  }
  // v3.36.10 · Si el error coincide con el patrón clásico de Google Translate
  // (removeChild/insertBefore sobre un nodo que GT reemplazó), es muy probable
  // que un reload limpio con lang=es + notranslate recupere la sesión.
  isGoogleTranslateCrash = () => {
    const m = String(this.state.error?.message || '').toLowerCase();
    return m.includes("failed to execute 'removechild'") ||
           m.includes("failed to execute 'insertbefore'") ||
           m.includes('the node to be removed is not a child');
  };
  handleReset = () => this.setState({ error: null });
  handleHome = () => { window.location.href = '/dashboard'; };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error) || 'Error desconocido';
    const gtCrash = this.isGoogleTranslateCrash();
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="error-boundary">
        <div className="max-w-xl w-full bg-white border rounded-lg shadow-sm p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 text-xl">!</div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Algo falló al cargar esta pantalla</h2>
              <p className="text-sm text-slate-600">Guardamos el detalle para revisarlo. Podés volver al inicio o reintentar.</p>
            </div>
          </div>
          {gtCrash && (
            <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900" data-testid="gt-crash-hint">
              <b>Detectamos el traductor del navegador.</b> Chrome / Google Translate puede
              romper esta app. Por favor desactivalo para este sitio:
              clic derecho sobre la página → <i>Traducir al...</i> → <i>Nunca traducir este sitio</i>.
              Luego recargá la página.
            </div>
          )}
          <details className="rounded-md bg-slate-50 border p-3 text-xs text-slate-700" data-testid="error-boundary-details">
            <summary className="cursor-pointer font-medium">Ver detalle técnico</summary>
            <pre className="whitespace-pre-wrap break-words mt-2">{msg}</pre>
          </details>
          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50"
              data-testid="error-boundary-retry">Reintentar</button>
            <button
              onClick={this.handleHome}
              className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800"
              data-testid="error-boundary-home">Volver al inicio</button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
